import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { axiosInstance, getCachedData, getApiErrorMessage } from "../App";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Camera, Calendar, Edit, Trash2, CalendarDays, Download, Users, Copy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import ExportButton from "../components/common/ExportButton";
import DataExportButton from "../components/common/DataExportButton";
import SearchableChildSelect from "../components/common/SearchableChildSelect";
import { PageLayout } from "../components/layout/Sidebar";
import useFeaturePermissions from "../hooks/useFeaturePermissions";
import { useModuleAccess } from "../hooks/useModuleAccess";
import { LazyImage, optimizeCloudinaryUrl } from "../components/common/LazyImage";
import { FloatingElements, AnimatedHeader, AnimatedCard } from "../components/ui/AnimatedElements";
import CalendarWithDots from "../components/common/CalendarWithDots";
import { getLocalDateString } from "../lib/utils";
import { confirmIfBeforeNoon } from "../lib/timeUtils";
import AlphabetFilter from "../components/AlphabetFilter";
import StatisticsModal, { StatisticsSummary } from "../components/StatisticsModal";
import RestoreButton from "../components/RestoreButton";

// Helper function to download image
const downloadImage = async (imageUrl, filename = "gambar") => {
  try {
    toast.loading("Memuat turun gambar...", { id: "download" });
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}_${getLocalDateString()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    toast.success("Gambar berjaya dimuat turun!", { id: "download" });
  } catch (error) {
    console.error("Download error:", error);
    toast.error("Gagal memuat turun gambar", { id: "download" });
  }
};

export default function ActivitiesPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasAccess } = useFeaturePermissions(user?.role);
  const { isModuleOn } = useModuleAccess(user?.role);
  const showSubjectField = isModuleOn('activity_subject');
  const [activities, setActivities] = useState([]);
  const [children, setChildren] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editingActivity, setEditingActivity] = useState(null);
  const [editingClassGroup, setEditingClassGroup] = useState(null); // For bulk edit by class
  const [selectedDate, setSelectedDate] = useState(() => searchParams.get('date') || getLocalDateString());
  const [selectedClass, setSelectedClass] = useState("all");
  const [nameFilter, setNameFilter] = useState(""); // NEW: For alphabet filter
  const [showStatsModal, setShowStatsModal] = useState(false); // NEW: For statistics modal
  const [statsData, setStatsData] = useState(null); // NEW: Statistics data
  const [viewImageUrl, setViewImageUrl] = useState(null);
  const [addMode, setAddMode] = useState("individual");
  const [selectedClassForBulk, setSelectedClassForBulk] = useState("");
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [excludedChildIds, setExcludedChildIds] = useState([]); // New: for excluding children
  const [excludeSearch, setExcludeSearch] = useState(""); // New: search for exclusion
  const [highlightedActivityId, setHighlightedActivityId] = useState(null); // For deep link highlighting
  const highlightedCardRef = useRef(null);
  
  // State untuk Duplicate Feature
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  const [duplicateActivity, setDuplicateActivity] = useState(null);
  const [duplicateMode, setDuplicateMode] = useState("individual"); // "individual" or "multiple"
  const [selectedChildrenForDuplicate, setSelectedChildrenForDuplicate] = useState([]);
  const [duplicating, setDuplicating] = useState(false);
  
  // State untuk Bulk Delete Dialog
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [bulkDeleteData, setBulkDeleteData] = useState(null); // {className, activityType, date, activities, childrenNames}
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // State untuk Duplicate Warning Dialog (P1)
  const [showDupWarningDialog, setShowDupWarningDialog] = useState(false);
  const [dupWarningData, setDupWarningData] = useState(null);
  const [pendingFormData, setPendingFormData] = useState(null);
  
  const [formData, setFormData] = useState({
    child_id: "",
    activity_type: "",
    time: "",
    description: "",
    notes: "",
    date: getLocalDateString(),
    learning_domains: []  // Changed to array for multiple selections
  });
  const [photoFile, setPhotoFile] = useState(null);

  const [availableClasses, setAvailableClasses] = useState([]);

  // Handle deep link from notifications
  useEffect(() => {
    const dateParam = searchParams.get('date');
    const highlightParam = searchParams.get('highlight');
    
    if (dateParam) {
      setSelectedDate(dateParam);
    }
    if (highlightParam) {
      setHighlightedActivityId(highlightParam);
      // Clear highlight after 5 seconds
      setTimeout(() => setHighlightedActivityId(null), 5000);
      // Clear the search params after processing
      setSearchParams({});
    }
  }, [searchParams, setSearchParams]);
  
  // Save selectedClass to localStorage for StatisticsSummary component
  useEffect(() => {
    localStorage.setItem('selectedClass', selectedClass);
  }, [selectedClass]);

  // Scroll to highlighted card when it's set
  useEffect(() => {
    if (highlightedActivityId && highlightedCardRef.current) {
      setTimeout(() => {
        highlightedCardRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 500);
    }
  }, [highlightedActivityId, activities]);

  const fetchChildren = useCallback(async () => {
    try {
      // Check cache first for instant display
      const cacheKey = '/children';
      const cached = getCachedData(cacheKey);
      if (cached) {
        // Use cached data immediately
        let filteredByBranch = cached;
        if (user.role === 'guru' || user.role === 'admin') {
          filteredByBranch = cached.filter(c => c.branch === user.branch);
        }
        setChildren(filteredByBranch);
        let uniqueClasses = [...new Set(filteredByBranch.map(c => c.class_name).filter(Boolean))];
        if (user.role === 'guru' && user.assigned_classes && user.assigned_classes.length > 0) {
          uniqueClasses = uniqueClasses.filter(className => user.assigned_classes.includes(className));
        }
        setAvailableClasses(uniqueClasses.sort());
      }
      
      // Then fetch fresh data in background
      const response = await axiosInstance.get('/children');
      const allChildren = response.data;
      
      // Filter children by branch for guru/admin
      let filteredByBranch = allChildren;
      if (user.role === 'guru' || user.role === 'admin') {
        filteredByBranch = allChildren.filter(c => c.branch === user.branch);
      }
      
      setChildren(filteredByBranch);
      
      // Extract unique class names from filtered children
      let uniqueClasses = [...new Set(filteredByBranch.map(c => c.class_name).filter(Boolean))];
      
      // For guru, further filter by assigned classes
      if (user.role === 'guru' && user.assigned_classes && user.assigned_classes.length > 0) {
        uniqueClasses = uniqueClasses.filter(className => 
          user.assigned_classes.includes(className)
        );
      }
      
      setAvailableClasses(uniqueClasses.sort());
    } catch (error) {
      toast.error("Ralat mendapatkan data kanak-kanak");
    }
  }, [user]);

  const fetchActivities = useCallback(async () => {
    try {
      // Use enhanced endpoint with name filter
      let url = `/activities/enhanced?date=${selectedDate}`;
      if (nameFilter) {
        url += `&name_filter=${encodeURIComponent(nameFilter)}`;
      }
      if (selectedClass && selectedClass !== 'all') {
        url += `&class_name=${encodeURIComponent(selectedClass)}`;
      }
      const response = await axiosInstance.get(url);
      setActivities(response.data);
    } catch (error) {
      toast.error("Ralat mendapatkan aktiviti");
    }
  }, [selectedDate, nameFilter, selectedClass]);

  useEffect(() => {
    fetchChildren();
    fetchActivities();
  }, [fetchChildren, fetchActivities]);

  // Use useMemo instead of useEffect + setState for filtering
  const filteredChildren = useMemo(() => {
    if (selectedClass === "all") {
      return children;
    }
    return children.filter(c => c.class_name === selectedClass);
  }, [selectedClass, children]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Jika medan Jenis Subjek dimatikan (kawalan module) dan tiada nilai, guna nilai lalai
    if (!showSubjectField && !formData.activity_type) {
      formData.activity_type = "Umum";
    }
    
    // Warning if submitting before 12 PM
    if (!confirmIfBeforeNoon()) return;
    
    // Validate minimum 2 learning domains when activity type is selected
    if (formData.activity_type && formData.learning_domains.length < 2) {
      toast.error("Sila pilih sekurang-kurangnya 2 bidang perkembangan");
      return;
    }
    
    // Hasil Pembelajaran is mandatory before save/submit
    if (!formData.notes || !formData.notes.trim()) {
      toast.error("Sila isi Hasil Pembelajaran sebelum simpan/hantar aktiviti");
      return;
    }
    
    // Bulk EDIT for class group
    if (editingClassGroup && editingClassGroup.activities.length > 0) {
      setBulkSubmitting(true);
      
      let successCount = 0;
      let failCount = 0;
      
      for (const act of editingClassGroup.activities) {
        try {
          const updateData = {
            ...formData,
            child_id: act.child_id // Keep original child_id
          };
          
          await axiosInstance.put(`/activities/${act.id}`, updateData);
          
          // Upload photo for each activity if photo exists
          if (photoFile) {
            try {
              const photoFormData = new FormData();
              photoFormData.append('file', photoFile);
              await axiosInstance.post(`/activities/${act.id}/photo`, photoFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (photoError) {
              console.error(`Failed to upload photo for activity ${act.id}:`, photoError);
            }
          }
          
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      
      setBulkSubmitting(false);
      setPhotoFile(null); // Clear photo after upload
      
      if (successCount > 0) {
        toast.success(`${successCount} aktiviti berjaya dikemaskini untuk kelas ${editingClassGroup.className}!` + (photoFile ? ' (dengan gambar)' : ''));
      }
      if (failCount > 0) {
        toast.error(`${failCount} aktiviti gagal dikemaskini`);
      }
      
      setShowDialog(false);
      setEditingClassGroup(null);
      setFormData({
        child_id: "",
        activity_type: "",
        time: "",
        description: "",
        notes: "",
        date: getLocalDateString(),
        learning_domains: []
      });
      setAddMode("individual");
      setSelectedClassForBulk("");
      fetchActivities();
      return;
    }
    
    // Bulk add for entire class or all classes
    if (addMode === "class" && (selectedClassForBulk || selectedClassForBulk === "all") && !editingActivity && !editingClassGroup) {
      setBulkSubmitting(true);
      
      // Get children based on class selection
      let classChildren;
      if (selectedClassForBulk === "all") {
        // All classes - get all children
        classChildren = children;
      } else {
        classChildren = children.filter(c => c.class_name === selectedClassForBulk);
      }
      
      // Apply exclusions
      classChildren = classChildren.filter(c => !excludedChildIds.includes(c.id));
      
      if (classChildren.length === 0) {
        toast.error("Tiada kanak-kanak untuk ditambah aktiviti (semua dikecualikan atau kelas kosong)");
        setBulkSubmitting(false);
        return;
      }
      
      let successCount = 0;
      let failCount = 0;
      
      for (const child of classChildren) {
        try {
          const response = await axiosInstance.post('/activities', {
            ...formData,
            child_id: child.id
          });
          
          // Upload photo for each record if photo exists
          if (photoFile && response.data.id) {
            try {
              const photoFormData = new FormData();
              photoFormData.append('file', photoFile);
              await axiosInstance.post(`/activities/${response.data.id}/photo`, photoFormData, {
                headers: { 'Content-Type': 'multipart/form-data' }
              });
            } catch (photoError) {
              console.error(`Failed to upload photo for child ${child.id}:`, photoError);
            }
          }
          
          successCount++;
        } catch (error) {
          failCount++;
        }
      }
      
      setBulkSubmitting(false);
      
      const classLabel = selectedClassForBulk === "all" ? "semua kelas" : selectedClassForBulk;
      if (successCount > 0) {
        toast.success(`${successCount} aktiviti berjaya ditambah untuk ${classLabel}!`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} aktiviti gagal ditambah`);
      }
      
      setShowDialog(false);
      setFormData({
        child_id: "",
        activity_type: "",
        time: "",
        description: "",
        notes: "",
        date: getLocalDateString(),
        learning_domains: []
      });
      setPhotoFile(null);
      setAddMode("individual");
      setSelectedClassForBulk("");
      setExcludedChildIds([]);
      setExcludeSearch("");
      fetchActivities();
      return;
    }
    
    // Individual add/edit
    try {
      let response;
      if (editingActivity) {
        response = await axiosInstance.put(`/activities/${editingActivity.id}`, formData);
        toast.success("Aktiviti berjaya dikemaskini!");
      } else {
        try {
          response = await axiosInstance.post('/activities', formData);
          toast.success("Aktiviti berjaya ditambah!");
        } catch (dupError) {
          // Check if it's a duplicate warning
          if (dupError.response?.status === 409 && dupError.response?.data?.detail?.is_duplicate_warning) {
            // Show proper dialog instead of window.confirm
            setDupWarningData(dupError.response.data.detail);
            setPendingFormData({...formData});
            setShowDupWarningDialog(true);
            return; // Don't close dialog yet
          } else {
            throw dupError; // Re-throw other errors
          }
        }
      }
      
      if (photoFile) {
        const photoFormData = new FormData();
        photoFormData.append('file', photoFile);
        await axiosInstance.post(`/activities/${response.data.id}/photo`, photoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowDialog(false);
      setEditingActivity(null);
      setFormData({
        child_id: "",
        activity_type: "",
        time: "",
        description: "",
        notes: "",
        date: getLocalDateString(),
        learning_domains: []
      });
      setPhotoFile(null);
      setAddMode("individual");
      setSelectedClassForBulk("");
      fetchActivities();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Ralat menyimpan aktiviti"));
    }
  };

  const handleEdit = (activity) => {
    setEditingActivity(activity);
    // Handle backward compatibility - convert old single value to array
    let learningDomainsValue = [];
    if (activity.learning_domains && Array.isArray(activity.learning_domains)) {
      learningDomainsValue = activity.learning_domains;
    } else if (activity.learning_domain) {
      learningDomainsValue = [activity.learning_domain];
    }
    
    setFormData({
      child_id: activity.child_id,
      activity_type: activity.activity_type,
      time: activity.time,
      description: activity.description,
      notes: activity.notes || "",
      date: activity.date,
      learning_domains: learningDomainsValue
    });
    setShowDialog(true);
  };

  // Handle Duplicate Activity
  const handleOpenDuplicate = (activity) => {
    setDuplicateActivity(activity);
    setDuplicateMode("individual");
    setSelectedChildrenForDuplicate([]);
    setShowDuplicateDialog(true);
  };

  const handleDuplicateActivity = async () => {
    if (!duplicateActivity) return;
    
    setDuplicating(true);
    try {
      const childIds = duplicateMode === "individual" 
        ? [duplicateActivity.child_id]
        : selectedChildrenForDuplicate;
      
      if (childIds.length === 0) {
        toast.error("Sila pilih sekurang-kurangnya satu kanak-kanak");
        setDuplicating(false);
        return;
      }

      let successCount = 0;
      let errorCount = 0;

      for (const childId of childIds) {
        try {
          await axiosInstance.post('/activities?force=true', {
            child_id: childId,
            activity_type: duplicateActivity.activity_type,
            time: duplicateActivity.time,
            description: duplicateActivity.description,
            notes: duplicateActivity.notes || "",
            date: duplicateActivity.date,
            title: duplicateActivity.title || "",
            learning_domains: duplicateActivity.learning_domains || [],
            photo_url: duplicateActivity.photo_url || ""
          });
          successCount++;
        } catch (err) {
          errorCount++;
          console.error(`Failed to duplicate for child ${childId}:`, err);
        }
      }

      if (successCount > 0) {
        toast.success(`Berjaya duplicate ${successCount} aktiviti!`);
        fetchActivities();
      }
      if (errorCount > 0) {
        toast.error(`${errorCount} aktiviti gagal di-duplicate`);
      }

      setShowDuplicateDialog(false);
      setDuplicateActivity(null);
      setSelectedChildrenForDuplicate([]);
    } catch (error) {
      toast.error("Ralat semasa duplicate aktiviti");
    } finally {
      setDuplicating(false);
    }
  };

  const toggleChildForDuplicate = (childId) => {
    setSelectedChildrenForDuplicate(prev => 
      prev.includes(childId) 
        ? prev.filter(id => id !== childId)
        : [...prev, childId]
    );
  };

  const selectAllChildrenForDuplicate = (className) => {
    const classChildren = children.filter(c => c.class_name === className);
    const classChildIds = classChildren.map(c => c.id);
    setSelectedChildrenForDuplicate(prev => {
      const newSelection = [...prev];
      classChildIds.forEach(id => {
        if (!newSelection.includes(id)) {
          newSelection.push(id);
        }
      });
      return newSelection;
    });
  };

  // Handle Duplicate Warning Confirm (P1: Force create + send notifications)
  const handleDupWarningConfirm = async () => {
    if (!pendingFormData) return;
    try {
      const response = await axiosInstance.post('/activities?force=true', pendingFormData);
      toast.success("Aktiviti berjaya ditambah (duplikat dikonfirm)!");
      
      if (photoFile && response.data?.id) {
        const photoFormData = new FormData();
        photoFormData.append('file', photoFile);
        await axiosInstance.post(`/activities/${response.data.id}/photo`, photoFormData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      setShowDupWarningDialog(false);
      setDupWarningData(null);
      setPendingFormData(null);
      setShowDialog(false);
      setEditingActivity(null);
      setFormData({
        child_id: "", activity_type: "", time: "", description: "", notes: "",
        date: getLocalDateString(), learning_domains: []
      });
      setPhotoFile(null);
      fetchActivities();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Ralat menyimpan aktiviti"));
    }
  };

  const handleDupWarningCancel = () => {
    setShowDupWarningDialog(false);
    setDupWarningData(null);
    setPendingFormData(null);
  };

  // Handle bulk edit for class group
  const handleBulkEditClass = (activity, className) => {
    // Find all activities with same type, time, date for this class
    const classActivities = activities.filter(a => {
      const child = children.find(c => c.id === a.child_id);
      return child?.class_name === className && 
             a.activity_type === activity.activity_type &&
             a.time === activity.time &&
             a.date === activity.date;
    });
    
    if (classActivities.length === 0) {
      toast.error("Tiada aktiviti untuk diedit");
      return;
    }
    
    setEditingClassGroup({
      activities: classActivities,
      className: className,
      originalData: activity
    });
    
    // Handle backward compatibility for learning domains
    let learningDomainsValue = [];
    if (activity.learning_domains && Array.isArray(activity.learning_domains)) {
      learningDomainsValue = activity.learning_domains;
    } else if (activity.learning_domain) {
      learningDomainsValue = [activity.learning_domain];
    }
    
    setFormData({
      child_id: "", // Not needed for class edit
      activity_type: activity.activity_type,
      time: activity.time,
      description: activity.description,
      notes: activity.notes || "",
      date: activity.date,
      learning_domains: learningDomainsValue
    });
    setAddMode("class");
    setSelectedClassForBulk(className);
    setShowDialog(true);
  };

  // Handle bulk delete for class group - Show confirmation dialog first
  const handleBulkDeleteClass = async (activity, className) => {
    // Find all matching activities for this class, type, and date
    const classActivities = activities.filter(a => {
      const child = children.find(c => c.id === a.child_id);
      const matchClass = child?.class_name === className;
      const matchActivityType = a.activity_type === activity.activity_type;
      const matchDate = a.date === activity.date;
      return matchClass && matchActivityType && matchDate;
    });
    
    if (classActivities.length === 0) {
      toast.error("Tiada aktiviti untuk dipadam");
      return;
    }
    
    // Get children names for display
    const childrenNames = classActivities.map(a => {
      const child = children.find(c => c.id === a.child_id);
      return child?.name || 'Unknown';
    });
    
    // Show detailed confirmation dialog
    setBulkDeleteData({
      className,
      activityType: activity.activity_type,
      date: activity.date,
      activities: classActivities,
      childrenNames: [...new Set(childrenNames)], // Unique names
      time: activity.time
    });
    setShowBulkDeleteDialog(true);
  };
  
  // Execute bulk delete after confirmation
  const executeBulkDelete = async () => {
    if (!bulkDeleteData) return;
    
    setBulkDeleting(true);
    let successCount = 0;
    let failCount = 0;
    
    for (const act of bulkDeleteData.activities) {
      try {
        await axiosInstance.delete(`/activities/${act.id}`);
        successCount++;
      } catch (error) {
        console.error(`[BulkDelete] Failed to delete activity ${act.id}:`, error);
        failCount++;
      }
    }
    
    setBulkDeleting(false);
    setShowBulkDeleteDialog(false);
    setBulkDeleteData(null);
    
    if (successCount > 0) {
      toast.success(`✅ ${successCount} aktiviti berjaya dipadam!`);
    }
    if (failCount > 0) {
      toast.error(`❌ ${failCount} aktiviti gagal dipadam`);
    }
    
    fetchActivities();
  };

  const handleDelete = async (activityId) => {
    if (!window.confirm("Adakah anda pasti mahu memadam aktiviti ini?")) return;
    
    try {
      await axiosInstance.delete(`/activities/${activityId}`);
      toast.success("Aktiviti berjaya dipadam!");
      fetchActivities();
    } catch (error) {
      toast.error("Ralat memadam aktiviti");
    }
  };

  const activityTypes = [
    "Mathematic",
    "Vocab",
    "Motorskill",
    "Sensory Play",
    "Health & Cookery",
    "Pretend Play",
    "Gym Sport",
    "Science",
    "Moral",
    "English",
    "Bahasa Melayu",
    "Art & Craft",
    "Self - Help Skill"
  ];

  const learningDomains = [
    "Perkembangan Awal Matematik & Pemikiran Logik",
    "Perkembangan Bahasa, Komunikasi & Literasi Awal",
    "Perkembangan Sahsiah, Sosioemosi & Kerohanian",
    "Perkembangan Motor Halus & Psikomotor",
    "Perkembangan Deria & Pemahaman Persekitaran",
    "Perkembangan Kreativiti & Estetika",
    "Perkembangan Pemakanan Seimbang",
    "Perkembangan Fizikal & Motor Kasar"
  ];

  return (
    <PageLayout user={user} onLogout={onLogout} hasAccess={hasAccess}>
      <FloatingElements variant="default" />
      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header - Space Explorer Style */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
          <AnimatedHeader 
            emoji="🎨"
            title="Aktiviti Harian"
            subtitle="🎪 Rekod aktiviti pembelajaran yang menyeronokkan!"
            delay={100}
          />
          <div className="flex items-center gap-2 flex-wrap">
            {(user.role === 'admin' || user.role === 'guru') && (
              <DataExportButton 
                dataType="activities"
                childrenList={children}
                classes={availableClasses}
                title="Export"
              />
            )}
            {(user.role === 'admin' || user.role === 'guru') && (
              <Button
                data-testid="add-activity-button"
                onClick={() => setShowDialog(true)}
                className="bg-gradient-to-r from-purple-400 to-pink-500 hover:from-purple-500 hover:to-pink-600 text-white rounded-2xl shadow-lg font-black hover:scale-105 transition-all text-sm px-3 py-2"
              >
                <Plus className="w-4 h-4 mr-1" />
                Tambah
              </Button>
            )}
          </div>
        </div>

        {/* Date Filter - Space Explorer Card */}
        <AnimatedCard className="mb-6" delay={200} color="purple">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-lg">
              <span className="text-2xl">📅</span>
            </div>
            <div className="flex-1">
              <label className="text-sm font-black text-gray-600 mb-1 block">Pilih Tarikh</label>
              <div className="flex items-center gap-3">
                <div 
                  data-testid="date-display"
                  className="flex-1 px-4 py-3 border-3 border-purple-200 rounded-2xl bg-white font-bold text-gray-700"
                >
                  {new Date(selectedDate).toLocaleDateString('ms-MY', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </div>
                <CalendarWithDots 
                  dataType="activities"
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                  dotColor="bg-purple-500"
                  user={user}
                />
              </div>
            </div>
          </div>
        </AnimatedCard>

        {/* Class Filter - Space Explorer Style */}
        {(user.role === "admin" || user.role === "guru" || user.role === "superadmin") && availableClasses.length > 0 && isModuleOn("filter_class_activities") && (
          <AnimatedCard className="mb-6" delay={300} color="pink">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-400 to-rose-500 flex items-center justify-center shadow-lg">
                <span className="text-xl">🏫</span>
              </div>
              <h3 className="font-black text-gray-700">Pilih Mengikut Kelas</h3>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setSelectedClass("all")}
                className={`px-4 py-2.5 rounded-2xl font-bold transition-all duration-300 text-sm ${
                  selectedClass === "all"
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-xl scale-105'
                    : 'bg-gray-100 text-gray-600 hover:bg-purple-100 hover:scale-105'
                }`}
              >
                🎨 Semua ({activities.length})
              </button>
              {availableClasses.map((className, idx) => {
                // Count activities for this class
                const classActivityCount = activities.filter(activity => {
                  const child = children.find(c => c.id === activity.child_id);
                  return child?.class_name === className;
                }).length;
                return (
                  <button
                    key={className}
                    onClick={() => setSelectedClass(className)}
                    className={`px-4 py-2.5 rounded-2xl font-bold transition-all duration-300 text-sm ${
                      selectedClass === className
                        ? 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-xl scale-105'
                        : 'bg-gray-100 text-gray-600 hover:bg-pink-100 hover:scale-105'
                    }`}
                  >
                    {['🍼', '🧸', '🎨', '🌈', '📚'][idx % 5]} {className.split('(')[0].trim()} ({classActivityCount})
                  </button>
                );
              })}
            </div>
          </AnimatedCard>
        )}

        {/* Alphabet Filter & Statistics - NEW FEATURES */}
        {(user.role === "admin" || user.role === "guru" || user.role === "superadmin") && (
          <>
            {isModuleOn("filter_name_activities") && (
            <AnimatedCard className="mb-6" delay={350} color="blue">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-400 to-cyan-500 flex items-center justify-center shadow-lg">
                  <span className="text-xl">🔤</span>
                </div>
                <h3 className="font-black text-gray-700">Cari Mengikut Nama</h3>
              </div>
              <AlphabetFilter
                onFilterChange={(filter) => {
                  if (filter.type === 'all') {
                    setNameFilter('');
                  } else {
                    setNameFilter(filter.value);
                  }
                }}
              />
            </AnimatedCard>
            )}

            {/* Statistics Summary */}
            {selectedClass !== 'all' && (
              <AnimatedCard className="mb-6" delay={380} color="purple">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-pink-500 flex items-center justify-center shadow-lg">
                      <span className="text-xl">📊</span>
                    </div>
                    <h3 className="font-black text-gray-700">Status Kemaskini</h3>
                  </div>
                  <StatisticsSummary
                    className={selectedClass}
                    date={selectedDate}
                    type="activity"
                    onOpenModal={(stats) => {
                      setStatsData(stats);
                      setShowStatsModal(true);
                    }}
                  />
                </div>
              </AnimatedCard>
            )}
          </>
        )}


        {/* Activities List */}
        <div className="space-y-4">
          {activities
            .filter(activity => {
              if (selectedClass === "all") return true;
              const child = children.find(c => c.id === activity.child_id);
              return child?.class_name === selectedClass;
            })
            .map((activity, idx) => {
            const child = children.find(c => c.id === activity.child_id);
            const isHighlighted = highlightedActivityId === activity.id;
            return (
              <div 
                key={activity.id}
                ref={isHighlighted ? highlightedCardRef : null}
              >
                <AnimatedCard
                  data-testid={`activity-card-${activity.id}`}
                  className={`hover:scale-[1.01] !p-5 ${isHighlighted ? 'ring-4 ring-pink-400 ring-offset-2 animate-pulse' : ''}`}
                  delay={400 + (idx * 50)}
                  color={['purple', 'pink', 'orange', 'blue', 'green'][idx % 5]}
                >
                <div className="flex flex-col lg:flex-row gap-4">
                  {activity.photo_url && (
                    <div 
                      className="w-full lg:w-32 h-32 rounded-2xl overflow-hidden flex-shrink-0 cursor-pointer group relative shadow-lg"
                        onClick={() => setViewImageUrl(activity.photo_url)}
                        title="Klik untuk lihat gambar besar"
                      >
                        {/* Animated gradient border */}
                        <div className="absolute inset-0 bg-gradient-to-r from-purple-400 via-pink-400 to-orange-400 rounded-2xl animate-pulse opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ padding: '3px' }}>
                          <div className="w-full h-full bg-white rounded-xl"></div>
                        </div>
                        <div className="relative w-full h-full transform transition-all duration-300 group-hover:scale-105">
                          <LazyImage
                            src={activity.photo_url}
                            alt="Activity"
                            className="w-full h-full object-cover rounded-2xl"
                            fallbackColor="bg-gradient-to-br from-purple-200 to-pink-200"
                          />
                          {/* Hover overlay with zoom icon */}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-2xl flex items-center justify-center">
                            <span className="text-white text-2xl opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300">🔍</span>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-xl text-gray-800">{showSubjectField ? activity.activity_type : "Aktiviti"}</h3>
                          <p className="text-sm text-purple-600 font-semibold">{activity.child_name || child?.name || 'Unknown'} {child?.class_name ? `- ${child.class_name}` : ''}</p>
                          
                          {/* Display learning domains - FULL TEXT with better styling */}
                          {(activity.learning_domains && activity.learning_domains.length > 0) && (
                            <div className="mt-3 space-y-1.5">
                              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Bidang Perkembangan:</p>
                              <div className="flex flex-wrap gap-1.5">
                                {activity.learning_domains.map((domain, idx) => (
                                  <span 
                                    key={idx} 
                                    className="text-xs bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 px-3 py-1.5 rounded-xl font-medium border border-blue-200 shadow-sm hover:shadow-md transition-shadow"
                                  >
                                    📚 {domain}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {activity.learning_domain && !activity.learning_domains?.length && (
                            <div className="mt-3">
                              <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">Bidang Perkembangan:</p>
                              <span className="text-xs bg-gradient-to-r from-blue-50 to-cyan-50 text-blue-700 px-3 py-1.5 rounded-xl font-medium border border-blue-200 inline-block">
                                📚 {activity.learning_domain}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex flex-col items-end gap-2">
                          <span className="px-3 py-1.5 bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 rounded-full text-sm font-bold border border-purple-200 shadow-sm whitespace-nowrap">
                            🕐 {(() => {
                              // Format time with AM/PM
                              if (!activity.time) return '-';
                              const [hours, minutes] = activity.time.split(':');
                              const hour = parseInt(hours);
                              const ampm = hour >= 12 ? 'PM' : 'AM';
                              const hour12 = hour % 12 || 12;
                              return `${hour12}:${minutes} ${ampm}`;
                            })()}
                          </span>
                          {(user.role === 'admin' || user.role === 'guru') && (
                            <div className="flex flex-wrap gap-1">
                              {/* Duplicate Button */}
                              <button
                                onClick={() => handleOpenDuplicate(activity)}
                                className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition-colors"
                                title="Duplicate aktiviti"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              {/* Individual Edit/Delete */}
                              <button
                                onClick={() => handleEdit(activity)}
                                className="p-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                                title="Edit individu"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(activity.id)}
                                className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                                title="Padam individu"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              {/* Class Edit/Delete - only show if child has class */}
                              {child?.class_name && (
                                <>
                                  <button
                                    onClick={() => handleBulkEditClass(activity, child.class_name)}
                                    className="p-2 bg-purple-500 hover:bg-purple-600 text-white rounded-lg transition-colors flex items-center gap-1"
                                    title={`Edit semua ${activity.activity_type} untuk kelas ${child.class_name}`}
                                  >
                                    <Users className="w-4 h-4" />
                                    <Edit className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => handleBulkDeleteClass(activity, child.class_name)}
                                    className="p-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors flex items-center gap-1"
                                    title={`Padam semua ${activity.activity_type} untuk kelas ${child.class_name}`}
                                  >
                                    <Users className="w-4 h-4" />
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                </>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      {activity.notes && (
                        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-3 border border-green-200">
                          <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-1">Hasil Pembelajaran:</p>
                          <p className="text-gray-700 text-sm">{activity.notes}</p>
                        </div>
                      )}
                      {/* Teacher Name • Timestamp - hidden for ibu_bapa */}
                      {user.role !== 'ibu_bapa' && (activity.teacher_name || activity.created_at) && (
                        <div className="mt-2 pt-2 border-t border-gray-100">
                          <p className="text-xs text-gray-400">
                            {activity.teacher_name && <span>{activity.teacher_name}</span>}
                            {activity.teacher_name && activity.created_at && <span> • </span>}
                            {activity.created_at && (
                              <span>{new Date(activity.created_at).toLocaleString('ms-MY', { 
                                day: 'numeric', 
                                month: 'short', 
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                timeZone: 'Asia/Kuala_Lumpur'
                              })}</span>
                            )}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </AnimatedCard>
              </div>
            );
          })}
          </div>

          {activities.length === 0 && (
            <AnimatedCard className="text-center" delay={400} color="purple">
              <div className="py-12 px-6">
                {/* Calendar icon with gradient background */}
                <div className="relative inline-block mb-4">
                  <div className="absolute inset-0 w-28 h-28 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full blur-xl opacity-50"></div>
                  <div className="relative w-24 h-24 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full flex items-center justify-center mx-auto">
                    <CalendarDays className="w-12 h-12 text-purple-400" />
                  </div>
                </div>
                <h3 className="text-lg font-bold text-gray-700 mb-2">Tiada Aktiviti</h3>
                <p className="text-gray-500 text-sm mb-1">Tiada aktiviti direkodkan untuk tarikh ini</p>
                <p className="text-xs text-gray-400">Pilih tarikh lain atau tunggu guru merekod</p>
              </div>
            </AnimatedCard>
          )}
        </div>

      <Dialog open={showDialog} onOpenChange={(open) => {
        setShowDialog(open);
        if (!open) {
          setEditingActivity(null);
          setEditingClassGroup(null);
          setFormData({
            child_id: "",
            activity_type: "",
            time: "",
            description: "",
            notes: "",
            date: getLocalDateString(),
            learning_domains: []
          });
          setPhotoFile(null);
          setAddMode("individual");
          setSelectedClassForBulk("");
          setExcludedChildIds([]);
          setExcludeSearch("");
        }
      }}>
        <DialogContent className="max-w-md max-h-[75vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingClassGroup 
                ? `Edit Aktiviti Kelas ${editingClassGroup.className} (${editingClassGroup.activities.length} rekod)` 
                : editingActivity 
                  ? "Edit Aktiviti" 
                  : "Tambah Aktiviti Baru"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 pb-4">
            
            {/* Add Mode Toggle */}
            {!editingActivity && (user.role === 'admin' || user.role === 'guru') && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Mod Tambah</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setAddMode("individual")}
                    className={`flex-1 py-2 px-4 rounded-xl font-medium text-sm transition-all ${
                      addMode === "individual"
                        ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    👤 Individu
                  </button>
                  <button
                    type="button"
                    onClick={() => setAddMode("class")}
                    className={`flex-1 py-2 px-4 rounded-xl font-medium text-sm transition-all ${
                      addMode === "class"
                        ? 'bg-gradient-to-r from-purple-400 to-purple-500 text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    👥 Ikut Kelas
                  </button>
                </div>
              </div>
            )}

            {/* Individual Mode */}
            {addMode === "individual" && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Kanak-kanak</label>
                <SearchableChildSelect
                  data-testid="activity-child-select"
                  childrenList={user.role === "admin" || user.role === "guru" ? filteredChildren : children}
                  value={formData.child_id}
                  onChange={(childId) => setFormData({...formData, child_id: childId})}
                  placeholder="Cari atau pilih kanak-kanak..."
                />
              </div>
            )}

            {/* Class Mode */}
            {addMode === "class" && !editingActivity && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Pilih Kelas</label>
                  <select
                    className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                    value={selectedClassForBulk}
                    onChange={(e) => {
                      setSelectedClassForBulk(e.target.value);
                      setExcludedChildIds([]); // Reset exclusions when class changes
                    }}
                    required={addMode === "class"}
                  >
                    <option value="">Pilih kelas</option>
                    <option value="all">🌟 Semua Kelas ({children.length} kanak-kanak)</option>
                    {availableClasses.filter(className => className !== "Pre-school").map(className => {
                      const count = children.filter(c => c.class_name === className).length;
                      return (
                        <option key={className} value={className} disabled={count === 0}>
                          {className} ({count} kanak-kanak)
                        </option>
                      );
                    })}
                  </select>
                </div>

                {/* Exclusion Section */}
                {selectedClassForBulk && (
                  <div className="bg-orange-50 rounded-xl p-4 border border-orange-200">
                    <label className="block text-sm font-semibold text-orange-700 mb-2">
                      🚫 Kecualikan Kanak-kanak (Pilihan)
                    </label>
                    <p className="text-xs text-orange-600 mb-3">
                      Cari dan pilih kanak-kanak yang tidak perlu menerima aktiviti ini
                    </p>
                    
                    {/* Search input */}
                    <div className="relative mb-3">
                      <input
                        type="text"
                        placeholder="Cari nama untuk dikecualikan..."
                        value={excludeSearch}
                        onChange={(e) => setExcludeSearch(e.target.value)}
                        className="w-full px-4 py-2 pl-10 border border-orange-200 rounded-lg text-sm focus:border-orange-400 focus:ring-1 focus:ring-orange-200"
                      />
                      <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    
                    {/* Children list for exclusion */}
                    <div className="max-h-40 overflow-y-auto space-y-1 bg-white rounded-lg p-2">
                      {(() => {
                        const targetChildren = selectedClassForBulk === "all" 
                          ? children 
                          : children.filter(c => c.class_name === selectedClassForBulk);
                        
                        const searchedChildren = excludeSearch 
                          ? targetChildren.filter(c => 
                              c.name?.toLowerCase().includes(excludeSearch.toLowerCase()) ||
                              c.nickname?.toLowerCase().includes(excludeSearch.toLowerCase())
                            )
                          : targetChildren;
                        
                        return searchedChildren.map(child => (
                          <label 
                            key={child.id} 
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                              excludedChildIds.includes(child.id) 
                                ? 'bg-red-100 text-red-700' 
                                : 'hover:bg-gray-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={excludedChildIds.includes(child.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setExcludedChildIds([...excludedChildIds, child.id]);
                                } else {
                                  setExcludedChildIds(excludedChildIds.filter(id => id !== child.id));
                                }
                              }}
                              className="w-4 h-4 text-red-500 border-gray-300 rounded focus:ring-red-400"
                            />
                            <span className={`text-sm ${excludedChildIds.includes(child.id) ? 'line-through' : ''}`}>
                              {child.name}
                            </span>
                            <span className="text-xs text-gray-400">
                              {selectedClassForBulk === "all" ? child.class_name : ''}
                            </span>
                          </label>
                        ));
                      })()}
                    </div>
                    
                    {/* Exclusion summary */}
                    {excludedChildIds.length > 0 && (
                      <div className="mt-2 flex items-center justify-between">
                        <p className="text-xs text-red-600">
                          {excludedChildIds.length} kanak-kanak dikecualikan
                        </p>
                        <button
                          type="button"
                          onClick={() => setExcludedChildIds([])}
                          className="text-xs text-red-600 hover:text-red-800 underline"
                        >
                          Reset
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* Summary */}
                {selectedClassForBulk && (
                  <p className="text-xs text-purple-600">
                    ✓ {(() => {
                      const targetChildren = selectedClassForBulk === "all" 
                        ? children 
                        : children.filter(c => c.class_name === selectedClassForBulk);
                      return targetChildren.length - excludedChildIds.length;
                    })()} kanak-kanak akan menerima aktiviti ini
                  </p>
                )}
              </div>
            )}

            {showSubjectField && (
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Jenis Subjek</label>
              <select
                data-testid="activity-type-select"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                value={formData.activity_type}
                onChange={(e) => setFormData({...formData, activity_type: e.target.value})}
                required
              >
                <option value="">Pilih jenis subjek</option>
                {activityTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Bidang Perkembangan Permata Negara
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Pilih <span className="font-bold text-purple-600">minimum 2 bidang</span> perkembangan berdasarkan Kurikulum Permata Negara
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto p-3 border-2 border-gray-200 rounded-xl bg-gray-50">
                {learningDomains.map((domain, index) => (
                  <label
                    key={domain}
                    className={`flex items-start gap-3 p-2 rounded-lg cursor-pointer transition-all ${
                      formData.learning_domains.includes(domain)
                        ? 'bg-purple-100 border border-purple-300'
                        : 'hover:bg-gray-100'
                    }`}
                  >
                    <input
                      type="checkbox"
                      data-testid={`learning-domain-checkbox-${index}`}
                      checked={formData.learning_domains.includes(domain)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData({
                            ...formData,
                            learning_domains: [...formData.learning_domains, domain]
                          });
                        } else {
                          setFormData({
                            ...formData,
                            learning_domains: formData.learning_domains.filter(d => d !== domain)
                          });
                        }
                      }}
                      className="w-4 h-4 mt-0.5 text-purple-500 border-gray-300 rounded focus:ring-purple-400"
                    />
                    <span className="text-sm text-gray-700">{domain}</span>
                  </label>
                ))}
              </div>
              {formData.activity_type && formData.learning_domains.length < 2 && (
                <p className="text-xs text-red-500 mt-2">
                  ⚠️ Sila pilih sekurang-kurangnya 2 bidang perkembangan
                </p>
              )}
              {formData.learning_domains.length >= 2 && (
                <p className="text-xs text-green-600 mt-2">
                  ✓ {formData.learning_domains.length} bidang dipilih
                </p>
              )}
            </div>

            {/* Masa & Tarikh - Stack on mobile */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Masa</label>
                <input
                  data-testid="activity-time-input"
                  type="time"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 transition-all outline-none text-base"
                  value={formData.time}
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Tarikh</label>
                <input
                  data-testid="activity-date-input"
                  type="date"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 transition-all outline-none text-base"
                  value={formData.date}
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Hasil Pembelajaran <span className="text-red-500">*</span></label>
              <textarea
                data-testid="activity-notes-input"
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-400 focus:ring-2 focus:ring-purple-200 transition-all outline-none"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                rows={3}
                placeholder="Nota perkembangan atau catatan guru... (wajib diisi)"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">Gambar (Pilihan)</label>
              <div className="flex items-center gap-3">
                <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-purple-400 cursor-pointer transition-all">
                  <Camera className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">
                    {photoFile ? photoFile.name : 'Pilih gambar'}
                  </span>
                  <input
                    data-testid="activity-photo-input"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => setPhotoFile(e.target.files[0])}
                  />
                </label>
              </div>
            </div>

            <Button
              data-testid="submit-activity-button"
              type="submit"
              disabled={bulkSubmitting}
              className="w-full bg-gradient-to-r from-purple-400 to-purple-500 hover:from-purple-500 hover:to-purple-600 text-white"
            >
              {bulkSubmitting ? "Menambah aktiviti..." : 
               editingActivity ? "Kemaskini Aktiviti" : 
               addMode === "class" ? `Tambah untuk Semua (${children.filter(c => c.class_name === selectedClassForBulk).length} kanak-kanak)` : 
               "Tambah Aktiviti"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Duplicate Warning Dialog (P1) */}
      <Dialog open={showDupWarningDialog} onOpenChange={(open) => {
        if (!open) handleDupWarningCancel();
      }}>
        <DialogContent className="max-w-md" data-testid="duplicate-warning-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Amaran Duplikat Dikesan
            </DialogTitle>
          </DialogHeader>
          
          {dupWarningData && (
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-gray-700 font-medium mb-2">{dupWarningData.message}</p>
                {dupWarningData.child_name && (
                  <p className="text-sm text-gray-500">Kanak-kanak: <strong>{dupWarningData.child_name}</strong></p>
                )}
                {dupWarningData.existing_description && (
                  <p className="text-sm text-gray-500 mt-1">Keterangan sedia ada: <em>{dupWarningData.existing_description}</em></p>
                )}
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
                <p className="text-xs text-blue-700">
                  Jika anda teruskan, notifikasi amaran akan dihantar kepada Admin, Pengurus Cawangan dan Superadmin.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  onClick={handleDupWarningCancel}
                  className="flex-1"
                  data-testid="dup-warning-cancel-btn"
                >
                  Batal
                </Button>
                <Button
                  onClick={handleDupWarningConfirm}
                  className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
                  data-testid="dup-warning-confirm-btn"
                >
                  Teruskan
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={(open) => {
        if (!open && !bulkDeleting) {
          setShowBulkDeleteDialog(false);
          setBulkDeleteData(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <Trash2 className="w-5 h-5" />
              Pengesahan Padam Aktiviti Kelas
            </DialogTitle>
          </DialogHeader>
          
          {bulkDeleteData && (
            <div className="space-y-4 py-4">
              {/* Summary Info */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 space-y-2">
                <div className="flex items-center gap-2 text-purple-800">
                  <Users className="w-5 h-5" />
                  <span className="font-bold">Kelas: {bulkDeleteData.className}</span>
                </div>
                <div className="flex items-center gap-2 text-purple-700">
                  <span className="text-lg">🎯</span>
                  <span><strong>Jenis Aktiviti:</strong> {showSubjectField ? bulkDeleteData.activityType : "Aktiviti"}</span>
                </div>
                <div className="flex items-center gap-2 text-purple-700">
                  <Calendar className="w-4 h-4" />
                  <span><strong>Tarikh:</strong> {bulkDeleteData.date}</span>
                </div>
              </div>
              
              {/* Children List */}
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="font-bold text-red-800 mb-2">
                  ⚠️ {bulkDeleteData.activities.length} rekod akan dipadam:
                </p>
                <div className="max-h-32 overflow-y-auto space-y-1">
                  {bulkDeleteData.childrenNames.map((name, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-red-700 text-sm">
                      <span>•</span>
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Warning */}
              <p className="text-sm text-gray-600 text-center italic">
                Tindakan ini tidak boleh dibatalkan!
              </p>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowBulkDeleteDialog(false);
                    setBulkDeleteData(null);
                  }}
                  disabled={bulkDeleting}
                >
                  Batal
                </Button>
                <Button
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white"
                  onClick={executeBulkDelete}
                  disabled={bulkDeleting}
                >
                  {bulkDeleting ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Memadam...
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4 mr-2" />
                      Ya, Padam {bulkDeleteData.activities.length} Rekod
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Viewer Modal */}
      <Dialog open={!!viewImageUrl} onOpenChange={(open) => {
        if (!open) setViewImageUrl(null);
      }}>
        <DialogContent className="max-w-3xl p-2">
          <DialogHeader>
            <DialogTitle className="text-center">Gambar Aktiviti</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center p-4 space-y-4">
            {viewImageUrl && (
              <>
                <img
                  src={optimizeCloudinaryUrl(viewImageUrl)}
                  alt="Activity"
                  className="max-w-full max-h-[60vh] object-contain rounded-xl shadow-lg"
                  loading="lazy"
                />
                <Button
                  data-testid="download-activity-image"
                  onClick={() => downloadImage(viewImageUrl, "aktiviti")}
                  className="bg-gradient-to-r from-green-400 to-emerald-500 hover:from-green-500 hover:to-emerald-600 text-white font-bold px-6 py-2 rounded-xl shadow-md"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Muat Turun Gambar
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate Activity Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="w-5 h-5 text-green-600" />
              Duplicate Aktiviti
            </DialogTitle>
          </DialogHeader>
          
          {duplicateActivity && (
            <div className="space-y-4">
              {/* Activity Info */}
              <div className="bg-gray-50 rounded-lg p-3 text-sm">
                <p className="font-semibold text-gray-800">{showSubjectField ? duplicateActivity.activity_type : "Aktiviti"}</p>
                <p className="text-gray-600">{duplicateActivity.description?.substring(0, 100)}</p>
                <p className="text-gray-500 text-xs mt-1">
                  {duplicateActivity.date} • {duplicateActivity.time}
                </p>
              </div>

              {/* Duplicate Mode */}
              <div className="space-y-3">
                <label className="font-semibold text-sm">Pilih Mod Duplicate:</label>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDuplicateMode("individual")}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      duplicateMode === "individual"
                        ? "bg-green-500 text-white border-green-500"
                        : "bg-white text-gray-700 border-gray-300 hover:border-green-400"
                    }`}
                  >
                    🧒 Individu Sama
                  </button>
                  <button
                    onClick={() => setDuplicateMode("multiple")}
                    className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${
                      duplicateMode === "multiple"
                        ? "bg-green-500 text-white border-green-500"
                        : "bg-white text-gray-700 border-gray-300 hover:border-green-400"
                    }`}
                  >
                    👥 Pilih Kanak-kanak
                  </button>
                </div>
              </div>

              {/* Show child selection if multiple mode */}
              {duplicateMode === "multiple" && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="font-semibold text-sm">Pilih Kanak-kanak:</label>
                    <span className="text-xs text-gray-500">
                      {selectedChildrenForDuplicate.length} dipilih
                    </span>
                  </div>
                  
                  {/* Quick select by class */}
                  <div className="flex flex-wrap gap-1">
                    {availableClasses.map(cls => (
                      <button
                        key={cls}
                        onClick={() => selectAllChildrenForDuplicate(cls)}
                        className="text-xs px-2 py-1 bg-purple-100 text-purple-700 rounded-full hover:bg-purple-200"
                      >
                        + {cls.split(' ')[0]}
                      </button>
                    ))}
                  </div>
                  
                  {/* Children list */}
                  <div className="max-h-48 overflow-y-auto border rounded-lg divide-y">
                    {children.map(child => (
                      <label
                        key={child.id}
                        className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-50 ${
                          selectedChildrenForDuplicate.includes(child.id) ? "bg-green-50" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedChildrenForDuplicate.includes(child.id)}
                          onChange={() => toggleChildForDuplicate(child.id)}
                          className="rounded text-green-600"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {child.full_name || child.name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {child.class_name || "Tiada kelas"}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => setShowDuplicateDialog(false)}
                  className="flex-1"
                  disabled={duplicating}
                >
                  Batal
                </Button>
                <Button
                  onClick={handleDuplicateActivity}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                  disabled={duplicating || (duplicateMode === "multiple" && selectedChildrenForDuplicate.length === 0)}
                >
                  {duplicating ? (
                    <>
                      <span className="animate-spin mr-2">⏳</span>
                      Duplicating...
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Duplicate {duplicateMode === "multiple" ? `(${selectedChildrenForDuplicate.length})` : ""}
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Statistics Modal - NEW FEATURE */}
      <StatisticsModal
        isOpen={showStatsModal}
        onClose={() => setShowStatsModal(false)}
        statistics={statsData}
        type="activity"
        className={selectedClass}
        onSelectChild={(child) => {
          setShowStatsModal(false);
          // Open form with selected child
          setFormData({
            child_id: child.id,
            activity_type: "",
            time: "",
            description: "",
            notes: "",
            date: selectedDate,
            learning_domains: []
          });
          setEditingActivity(null);
          setShowDialog(true);
        }}
      />
    </PageLayout>
  );
}